/**
 * <health-experts>
 *
 * Wechselt die gezeigte Person der Health-Experts-Section. Desktop-Fotoreihe und
 * Mobile-Bogen-Reihe sind zwei Tablists für dieselben Panels. Das Markup liegt
 * komplett in der Section, das Element setzt nur Attribute, Positionen und Texte.
 */
class HealthExperts extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('.hx-panel'));
    this.persons = Array.from(this.querySelectorAll('.hx-panel .hx-person'));
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.arcsRow = this.querySelector('.hx-arcs');
    this.photoRow = this.querySelector('.hx-tabs');
    this.track = this.querySelector('.hx-track');
    this.stage = this.querySelector('.hx-stage');
    this.counter = this.querySelector('[data-current]');

    if (this.panels.length < 2) return;

    this.activeIndex = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPrevious = this.onPrevious.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.updatePointer = this.updatePointer.bind(this);
    this.updateSelectionMarker = this.updateSelectionMarker.bind(this);
    this.onResize = this.onResize.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('slider:previous', this.onPrevious);
    this.addEventListener('slider:next', this.onNext);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    window.addEventListener('resize', this.onResize);
    if (this.arcsRow) this.arcsRow.addEventListener('scroll', this.updatePointer, { passive: true });
    if (this.photoRow) this.photoRow.addEventListener('scroll', this.updateSelectionMarker, { passive: true });

    this.onResize();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('slider:previous', this.onPrevious);
    this.removeEventListener('slider:next', this.onNext);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    window.removeEventListener('resize', this.onResize);
    if (this.arcsRow) this.arcsRow.removeEventListener('scroll', this.updatePointer);
    if (this.photoRow) this.photoRow.removeEventListener('scroll', this.updateSelectionMarker);
  }

  onResize() {
    this.updatePointer();
    this.updateSelectionMarker();
  }

  onClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;

    this.select(Number(tab.dataset.index));
  }

  onKeydown(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const step = event.key === 'ArrowRight' ? 1 : -1;
    this.select(this.activeIndex + step);

    // Fokus bleibt in der Liste, in der die Taste gedrückt wurde.
    const tablist = tab.closest('[role="tablist"]');
    const nextTab = tablist.querySelector(`[role="tab"][data-index="${this.activeIndex}"]`);
    if (nextTab) nextTab.focus();
  }

  onPrevious(event) {
    event.stopPropagation();
    this.select(this.activeIndex - 1);
  }

  onNext(event) {
    event.stopPropagation();
    this.select(this.activeIndex + 1);
  }

  // Im Theme-Editor springt die Section zur Person, deren Block angeklickt wurde.
  onBlockSelect(event) {
    const panel = event.target.closest('.hx-panel');
    if (panel) this.select(Number(panel.dataset.index));
  }

  select(index) {
    const count = this.panels.length;
    // Pfeile und Tastatur springen am Ende wieder zum Anfang.
    const nextIndex = (index + count) % count;
    if (nextIndex === this.activeIndex) return;

    this.placePersonAtPrevious(this.activeIndex, nextIndex);

    this.activeIndex = nextIndex;
    this.setAttribute('data-switched', '');

    this.panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== nextIndex;
    });

    this.tabs.forEach((tab) => {
      const isActive = Number(tab.dataset.index) === nextIndex;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive) this.scrollTabIntoView(tab);
    });

    if (this.counter) this.counter.textContent = nextIndex + 1;

    this.updatePointer();
    this.updateSelectionMarker();
  }

  // Scrollt nur die Leiste selbst horizontal, nicht die ganze Seite.
  scrollTabIntoView(tab) {
    const tablist = tab.closest('[role="tablist"]');
    if (!tablist || tablist.offsetParent === null) return;

    const listRect = tablist.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
    const scrollPadding = parseFloat(getComputedStyle(tablist).scrollPaddingInlineStart) || 0;

    let targetLeft;
    if (tabRect.left < listRect.left + scrollPadding) {
      targetLeft = tablist.scrollLeft + tabRect.left - listRect.left - scrollPadding;
    } else if (tabRect.right > listRect.right - scrollPadding) {
      const minimumLeft = tablist.scrollLeft + tabRect.right - listRect.right + scrollPadding;
      targetLeft = this.getSnapPositionFrom(tablist, minimumLeft);
    } else {
      return;
    }

    tablist.scrollTo({ left: targetLeft, behavior });
  }

  // Die Bogen-Reihe rastet an Bogenanfängen ein. Ohne diese Rundung schnappt sie bei kleinen Wegen an die alte Stelle zurück.
  getSnapPositionFrom(tablist, minimumLeft) {
    if (getComputedStyle(tablist).scrollSnapType === 'none') return minimumLeft;

    const listRect = tablist.getBoundingClientRect();
    const scrollPadding = parseFloat(getComputedStyle(tablist).scrollPaddingInlineStart) || 0;
    const snapPositions = Array.from(tablist.querySelectorAll('[role="tab"]')).map(
      (tab) => tablist.scrollLeft + tab.getBoundingClientRect().left - listRect.left - scrollPadding
    );

    const snapPosition = snapPositions.find((position) => position >= minimumLeft - 1);
    return snapPosition === undefined ? minimumLeft : snapPosition;
  }

  // Der Pfeil der Mobile-Karte zeigt auf die Mitte des aktiven Bogens.
  updatePointer() {
    if (!this.arcsRow || this.arcsRow.offsetParent === null || !this.stage) return;

    const activeArc = this.arcsRow.querySelector(`[role="tab"][data-index="${this.activeIndex}"]`);
    if (!activeArc) return;

    const media = activeArc.querySelector('.hx-arc__media') || activeArc;
    const mediaRect = media.getBoundingClientRect();
    const stageRect = this.stage.getBoundingClientRect();
    const centerX = mediaRect.left + mediaRect.width / 2 - stageRect.left;

    // Am Kartenrand bleibt der Pfeil innerhalb der Rundung.
    const minX = 24;
    const maxX = stageRect.width - 24;
    const pointerX = Math.min(Math.max(centerX, minX), maxX);

    this.stage.style.setProperty('--hx-pointer-x', `${pointerX}px`);
  }

  // Name und Balken stehen auf Desktop unter dem gewählten Foto und bleiben dabei innerhalb des Inhaltsbereichs.
  updateSelectionMarker() {
    if (!this.photoRow || this.photoRow.offsetParent === null || !this.track) return;

    const activePhoto = this.photoRow.querySelector(`[role="tab"][data-index="${this.activeIndex}"]`);
    if (!activePhoto) return;

    const trackRect = this.track.getBoundingClientRect();
    const photoRect = activePhoto.getBoundingClientRect();
    const photoLeft = photoRect.left - trackRect.left;

    const barLeft = this.clamp(photoLeft, 0, trackRect.width - photoRect.width);
    this.track.style.setProperty('--hx-bar-x', `${barLeft}px`);
    this.track.style.setProperty('--hx-bar-w', `${photoRect.width}px`);

    // Alle Namen bekommen die neue Position, damit auch die verborgenen nach einem Resize richtig stehen.
    this.persons.forEach((person) => {
      const personLeft = this.clamp(photoLeft, 0, trackRect.width - person.offsetWidth);
      person.style.setProperty('--hx-person-x', `${personLeft}px`);
    });
  }

  // Der neue Name startet genau dort, wo der bisherige gerade steht. So gleitet er nie von einer veralteten Stelle los.
  placePersonAtPrevious(previousIndex, nextIndex) {
    if (!this.photoRow || this.photoRow.offsetParent === null) return;

    const previousPerson = this.panels[previousIndex].querySelector('.hx-person');
    const nextPerson = this.panels[nextIndex].querySelector('.hx-person');
    if (!previousPerson || !nextPerson) return;

    // Alle Namen haben denselben Ursprung, nur die Verschiebung unterscheidet sich. Die Matrix liefert sie auch mitten im Gleiten.
    const nextShift = new DOMMatrixReadOnly(getComputedStyle(nextPerson).transform).m41;
    const nextLeftWithoutShift = nextPerson.getBoundingClientRect().left - nextShift;
    const startLeft = previousPerson.getBoundingClientRect().left - nextLeftWithoutShift;

    nextPerson.style.transition = 'none';
    nextPerson.style.setProperty('--hx-person-x', `${startLeft}px`);
    // Der Browser übernimmt die Startposition sofort, danach gleitet der Name im selben Frame zur neuen Person.
    void nextPerson.offsetWidth;
    nextPerson.style.transition = '';
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), Math.max(max, min));
  }
}

if (!customElements.get('health-experts')) {
  customElements.define('health-experts', HealthExperts);
}
